import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, editGroupTemplate } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { GroupTemplate } from '../../../../../types';

import { flushSync } from 'react-dom';
import { SubmitHandler, useForm } from 'react-hook-form';
import StepOne from '../create-template/step-one';
import StepTwo from '../create-template/step-two';
import StepThree from '../create-template/step-three';
import StepFour from '../create-template/step-four';
import CreateTemplateSteps from '../create-template/create-template-steps';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { GroupTemplateFormData } from '../create-template/group-template-form-data';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Close from '@mui/icons-material/Close';

interface EditTemplateDialogProps {
  open: boolean;
  handleClose: () => void;
  changeLogRoles: string[];
  selectedTemplate: GroupTemplate;
}

export default function EditTemplateDialog({
  open,
  handleClose,
  changeLogRoles,
  selectedTemplate,
}: EditTemplateDialogProps) {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [stepCompleted, setStepCompleted] = useState<boolean[]>([
    false,
    false,
    false,
    false,
  ]);
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    'General Information',
    'Settings',
    'Permissions',
    'Security Settings',
  ];

  const {
    control,
    handleSubmit,
    formState: { errors, isValidating, isSubmitting },
    trigger,
    reset,
    getValues,
    setValue,
    watch,
  } = useForm<GroupTemplateFormData>({
    values: {
      allowWebPosting: selectedTemplate.allowWebPosting,
      primaryLanguage: selectedTemplate.primaryLanguage,
      includeInGlobalAddressList: selectedTemplate.includeInGlobalAddressList,
      isArchived: selectedTemplate.isArchived,
      membersCanPostAsTheGroup: selectedTemplate.membersCanPostAsTheGroup,
      messageModerationLevel: selectedTemplate.messageModerationLevel,
      sendMessageDenyNotification: selectedTemplate.sendMessageDenyNotification,
      spamModerationLevel: selectedTemplate.spamModerationLevel,
      replyTo: selectedTemplate.replyTo,
      defaultMessageDenyNotificationText:
        selectedTemplate.defaultMessageDenyNotificationText,
      whoCanViewGroup: selectedTemplate.whoCanViewGroup,
      whoCanPostMessage: selectedTemplate.whoCanPostMessage,
      whoCanViewMembership: selectedTemplate.whoCanViewMembership,
      whoCanModerateMembers: selectedTemplate.whoCanModerateMembers,
      whoCanModerateContent: selectedTemplate.whoCanModerateContent,
      whoCanAssistContent: selectedTemplate.whoCanAssistContent,
      whoCanLeaveGroup: selectedTemplate.whoCanLeaveGroup,
      whoCanContactOwner: selectedTemplate.whoCanContactOwner,
      whoCanDiscoverGroup: selectedTemplate.whoCanDiscoverGroup,
      allowExternalMembers: selectedTemplate.allowExternalMembers,
      whoCanJoin: selectedTemplate.whoCanJoin,
      archiveOnly: selectedTemplate.archiveOnly,
      groupTemplateName: selectedTemplate.groupTemplateName,
      groupTemplateDesc: selectedTemplate.groupTemplateDesc,
      customReplyTo: selectedTemplate.customReplyTo,
      keyString: selectedTemplate.keyString,
      id: selectedTemplate.id,
      createdDate: selectedTemplate.createdDate,
      createdBy: selectedTemplate.createdBy,
      securityQuery: selectedTemplate.securityQuery ?? '',
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: (data: GroupTemplate) => {
      return editGroupTemplate(data, changeLogMessage);
    },
    onSuccess: (_returnData, data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Group Template edited successfully!',
      });

      queryClient.setQueryData<GroupTemplate[]>(
        [UserKeys.GroupTemplates],
        (old) => {
          if (old) {
            return old.map((template) =>
              template.keyString === data.keyString
                ? {
                    ...data,
                    keyString: selectedTemplate.keyString,
                  }
                : template
            );
          }
          return [];
        }
      );

      setChangeLogMessage('');
      handleClose();
      setActiveStep(0);
      reset();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue editing this group template. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.directory.group_templates.update_group_template, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.groupTemplates,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const onSubmit: SubmitHandler<GroupTemplateFormData> = () => {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        DirectoryRoles.EditGroupTemplates.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      const newTemplate = {
        ...getValues(),
      } as GroupTemplate;
      mutate(newTemplate);
    }
  };

  return (
    <Dialog open={open} maxWidth="lg" fullWidth>
      <IconButton
        sx={{ position: 'absolute', right: 0, top: 0, m: 1 }}
        onClick={() => {
          handleClose();
          reset();
          setActiveStep(0);
          setStepCompleted([false, false, false, false]);
        }}
      >
        <Close />
      </IconButton>
      <CreateTemplateSteps
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        steps={steps}
        trigger={trigger}
        stepCompleted={stepCompleted}
        setStepCompleted={setStepCompleted}
      />

      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                fontSize: 24,
                textAlign: 'center',
              }}
            >
              {steps[activeStep]}
            </Typography>
            <Box
              sx={{
                width: 96,
                height: 2,
                bgcolor: 'primary.main',
                mx: 'auto',
              }}
            ></Box>
          </Box>

          <Box
            sx={{
              pl: 3,
              height: 480,
              overflowY: 'auto',
              ...(steps[activeStep] === 'Security Settings' && {
                width: '100%',
                pl: 0,
              }),
            }}
          >
            {steps[activeStep] === 'General Information' && (
              <StepOne control={control} errors={errors} />
            )}
            {steps[activeStep] === 'Settings' && (
              <StepTwo
                control={control}
                setValue={setValue}
                watch={watch}
                errors={errors}
              />
            )}
            {steps[activeStep] === 'Permissions' && (
              <StepThree
                control={control}
                watch={watch}
                setValue={setValue}
                getValues={getValues}
              />
            )}
            {steps[activeStep] === 'Security Settings' && (
              <StepFour control={control} setValue={setValue} watch={watch} />
            )}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{ display: 'flex', justifyContent: 'space-between', p: 2 }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              if (document.startViewTransition) {
                document.startViewTransition(() => {
                  flushSync(() => {
                    setActiveStep((prevState) => prevState - 1);
                  });
                });
              } else {
                setActiveStep((prevState) => prevState - 1);
              }
            }}
            disabled={activeStep === 0}
          >
            Back
          </Button>
          <Button
            variant="contained"
            loading={isValidating || isSubmitting || isPending}
            type={activeStep === steps.length - 1 ? 'submit' : 'button'}
            onClick={async () => {
              if (activeStep === steps.length - 1) {
                //
              } else {
                const isValid = await trigger();

                if (isValid) {
                  if (document.startViewTransition) {
                    document.startViewTransition(() => {
                      flushSync(() => {
                        setActiveStep((prevState) => prevState + 1);
                        setStepCompleted((prevState) => {
                          const newState = [...prevState];
                          newState[activeStep] = true;
                          return newState;
                        });
                      });
                    });
                  } else {
                    setActiveStep((prevState) => prevState + 1);
                  }
                }
              }
            }}
          >
            {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
          </Button>
        </DialogActions>
        <ChangeLogDialog
          open={openChangeLogDialog}
          handleClose={() => setOpenChangeLogDialog(false)}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={() => {
            const newTemplate = {
              ...getValues(),
            } as GroupTemplate;
            mutate(newTemplate);
          }}
          mutateLoading={isPending}
        />
      </Box>
    </Dialog>
  );
}
